import { Component, StrictMode, type ErrorInfo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "maplibre-gl/dist/maplibre-gl.css";
import "./styles.css";

/**
 * A white screen is the worst thing this app can do to a surveyor standing in a
 * field with one bar of signal: there is nothing to screenshot, nothing to
 * describe, and nothing to retry. So a crash renders its own reason, in the
 * language the rest of the app is in, with a way out that does not need the
 * network.
 *
 * It also writes the failure into the document title and a hidden node, so that
 * a headless check can tell a rendered error page from a blank one.
 */
class Crash extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- there is nowhere else for this to go
    console.error("crash", error, info.componentStack);
    document.title = `Gagal memuat: ${error.message}`;
    const sink = document.createElement("pre");
    sink.id = "boot-error";
    sink.textContent = `${error.message}\n${info.componentStack ?? ""}`;
    document.body.append(sink);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="crash" role="alert">
        <h1>Aplikasi gagal memuat</h1>
        <p>
          Ada kesalahan yang tidak terduga. Data yang sudah tercatat tetap aman di
          server; yang belum terkirim akan mencoba lagi sendiri.
        </p>
        <pre className="crash__detail">{error.message}</pre>
        <button type="button" onClick={() => window.location.reload()}>
          Muat ulang
        </button>
      </div>
    );
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing from index.html");

createRoot(root).render(
  <StrictMode>
    <Crash>
      <App />
    </Crash>
  </StrictMode>,
);
